const GENERIC_RUNTIME_ERROR_MESSAGE = "Response returned an error code";

const GENERIC_HTTP_TITLES = new Set([
  "conflict",
  "bad request",
  "unauthorized",
  "forbidden",
  "not found",
  "internal server error",
  "one or more validation errors occurred.",
  "one or more validation errors occurred",
]);

export const EMAIL_ALREADY_IN_USE_MESSAGE = "This email is already in use.";

export type GetApiErrorMessageOptions = {
  conflictFallback?: string;
};

type ApiResult = {
  isSuccess?: boolean;
  error?: { message?: string | null } | null;
};

function firstTrimmedString(...values: unknown[]): string | null {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
}

function messageFromUnknownObject(value: unknown): string | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  return firstTrimmedString(
    record.message,
    record.description,
    record.detail,
    record.code,
  );
}

function isGenericHttpTitle(message: string): boolean {
  return GENERIC_HTTP_TITLES.has(message.trim().toLowerCase());
}

export function isEmailAlreadyInUseMessage(message: string): boolean {
  const lower = message.toLowerCase();
  if (
    lower.includes("emailalreadytaken") ||
    lower.includes("duplicateusername") ||
    lower.includes("duplicateemail")
  ) {
    return true;
  }

  if (lower.includes("already taken")) return true;

  if (!lower.includes("email")) return false;

  return (
    lower.includes("already") ||
    lower.includes("duplicate") ||
    lower.includes("in use")
  );
}

function humanizeParsedApiError(message: string): string {
  return isEmailAlreadyInUseMessage(message)
    ? EMAIL_ALREADY_IN_USE_MESSAGE
    : message;
}

function messagesFromKeyedErrors(value: unknown): string[] {
  if (!value || typeof value !== "object") return [];

  if (Array.isArray(value)) {
    return value
      .map((item) =>
        typeof item === "string" ? item.trim() : messageFromUnknownObject(item),
      )
      .filter((item): item is string => Boolean(item));
  }

  return Object.values(value as Record<string, unknown>).flatMap((entry) => {
    if (typeof entry === "string" && entry.trim()) return [entry.trim()];
    if (Array.isArray(entry)) {
      return entry
        .map((item) => (typeof item === "string" ? item.trim() : null))
        .filter((item): item is string => Boolean(item));
    }
    const nested = messageFromUnknownObject(entry);
    return nested ? [nested] : [];
  });
}

function parseApiErrorBody(responseData: unknown): string | null {
  if (!responseData) return null;

  if (typeof responseData === "string" && responseData.trim()) {
    return responseData.trim();
  }

  if (typeof responseData !== "object") return null;

  const record = responseData as Record<string, unknown>;
  const nestedErrorMessage = messageFromUnknownObject(record.error);
  if (nestedErrorMessage) return nestedErrorMessage;

  const fromErrors = messagesFromKeyedErrors(record.errors);
  if (fromErrors.length > 0) {
    const topMessage = firstTrimmedString(record.message);
    if (
      topMessage &&
      !isGenericHttpTitle(topMessage) &&
      !fromErrors.includes(topMessage)
    ) {
      return [topMessage, ...fromErrors].join(". ");
    }
    return fromErrors.join(". ");
  }

  if (typeof record.message === "string" && record.message.trim()) {
    const parts = [record.message.trim()];

    if (
      record.validationErrors &&
      typeof record.validationErrors === "object"
    ) {
      const validationMessages = messagesFromKeyedErrors(
        record.validationErrors,
      );

      if (validationMessages.length > 0) {
        parts.push(validationMessages.join(". "));
      }
    }

    return parts.join(". ");
  }

  if (typeof record.detail === "string" && record.detail.trim()) {
    return record.detail.trim();
  }

  if (typeof record.title === "string" && record.title.trim()) {
    return record.title.trim();
  }

  if (typeof record.error === "string" && record.error.trim()) {
    return record.error.trim();
  }

  if (record.validationErrors && typeof record.validationErrors === "object") {
    const validationMessages = messagesFromKeyedErrors(record.validationErrors);

    if (validationMessages.length > 0) {
      return validationMessages.join(". ");
    }
  }

  return null;
}

/** Reads a backend error body without rewriting it into a generic fallback. */
export function readApiErrorMessage(data: unknown): string | null {
  const parsed = parseApiErrorBody(data);
  if (!parsed || isGenericHttpTitle(parsed)) return null;
  return parsed.trim();
}

export function throwIfResultFailed(
  result: ApiResult | null | undefined,
  fallbackMessage: string,
): void {
  if (!result || result.isSuccess !== false) return;

  throw new Error(
    humanizeParsedApiError(result.error?.message?.trim() || fallbackMessage),
  );
}

export function setEmailAlreadyInUseFieldError(
  setError: (name: "email", error: { type: string; message: string }) => void,
  message: string,
): void {
  if (!isEmailAlreadyInUseMessage(message)) return;
  setError("email", { type: "server", message: EMAIL_ALREADY_IN_USE_MESSAGE });
}

export async function getApiErrorMessage(
  error: unknown,
  fallbackMessage: string = "An error occurred",
  options?: GetApiErrorMessageOptions,
): Promise<string> {
  if (error && typeof error === "object" && "response" in error) {
    const response = (error as { response?: Response }).response;

    if (response && typeof response.json === "function") {
      try {
        const bodySource =
          typeof response.clone === "function" ? response.clone() : response;
        const responseData = await bodySource.json();
        const parsed = parseApiErrorBody(responseData);

        if (parsed && !isGenericHttpTitle(parsed)) {
          return humanizeParsedApiError(parsed);
        }
      } catch {
        // Response body is not JSON or already consumed.
      }
    }

    if (response?.status === 409 && options?.conflictFallback) {
      return options.conflictFallback;
    }
  }

  if (error instanceof Error && error.message) {
    if (error.message !== GENERIC_RUNTIME_ERROR_MESSAGE) {
      return humanizeParsedApiError(error.message);
    }
  }

  return fallbackMessage;
}
